import { useReactiveVar } from '@apollo/client';
import { sortBy } from 'lodash';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { PlansContext, UserContext } from '../../contexts';
import { FetchUserQuery, UserTaxMethodsQuery } from '../../graphql/types';
import { identifyUser } from '../../lib/analytics';
import { getAttributionData } from '../../lib/attribution';
import { setGa4UserId } from '../../lib/ga4User';
import Auth, { tokenDataVar, TOKENTAX_ADMIN_ROLE, TOKENTAX_DEV_ROLE } from '../../lib/Auth';
import { useObject, useQueryAndRefetchOnUserChange } from '../../lib/hooks';
import { isStaffAnalyticsSession } from '../../lib/staffAnalytics';
import { getCurrentUserRole } from './currentUserRole';
import { USER_QUERY, USER_TAX_METHODS } from './queries';
import { useBatchJobsStatus } from './useBatchJobsStatus';

interface Props {
  children: ReactNode;
}

export default function UserProvider({ children }: Props) {
  const userQueryRes = useQueryAndRefetchOnUserChange<FetchUserQuery>(USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  const taxMethodsQueryRes = useQueryAndRefetchOnUserChange<UserTaxMethodsQuery>(USER_TAX_METHODS, {
    fetchPolicy: 'network-only',
  });

  const { data, refetch, loading } = userQueryRes;
  const tokenData = useReactiveVar(tokenDataVar);
  const isLoggedIn = Boolean(tokenData);

  const refetchIfLoggedIn = useCallback(() => {
    if (!isLoggedIn) return;
    refetch();
  }, [isLoggedIn, refetch]);

  // Refetch user data when the window regains focus
  // Many times TT employes will have multiple tabs open and will update user data in one tab
  useEffect(() => {
    window.addEventListener('focus', refetchIfLoggedIn);
    return () => {
      window.removeEventListener('focus', refetchIfLoggedIn);
    };
  }, [refetchIfLoggedIn]);

  const user = data?.user;
  const batchJobsStatus = useBatchJobsStatus();
  const hijackedUserId = Auth.getHijackedUserId();
  const isHijacked = Boolean(hijackedUserId);

  const taxMethods = taxMethodsQueryRes.data?.userTaxMethods?.methods ?? [];

  const tokenDataRoles = useMemo(() => tokenData?.roles ?? [], [tokenData?.roles]);
  const roles = useMemo(
    () => (loading ? tokenDataRoles : (user?.orgRoles?.map(({ role: { name } }) => name) ?? [])),
    [user?.orgRoles, loading, tokenDataRoles],
  );
  const isTokenTaxAdmin = tokenDataRoles.includes(TOKENTAX_ADMIN_ROLE);
  const isTokenTaxDev = tokenDataRoles.includes(TOKENTAX_DEV_ROLE);

  const currentUserRole = getCurrentUserRole(roles);
  const organizationId = user?.orgRoles.find((orgRole) => orgRole.organizationId)?.organizationId || null;

  const { taxDetails = [] } = user ?? {};
  const { cryptoOnlyPlans } = useContext(PlansContext);
  const rankedPlansFromHighest = sortBy(cryptoOnlyPlans, 'rank').reverse();
  const highestPlan = rankedPlansFromHighest.find((plan) =>
    taxDetails.some((taxDetail) => taxDetail.plan === plan.id),
  )?.id;

  useEffect(() => {
    if (!user?.id) return;
    // Force token refresh (override throttling) when the user is (un-)hijacked.
    // This ensures we immediately set hijacked users as active
    Auth.refreshToken({ force: true });
  }, [user?.id]);

  // Re-identify the user once per session on authenticated page loads so
  // RudderStack always has the anonymous_id → user_id link.
  useEffect(() => {
    if (!user?.id || !user?.email || isStaffAnalyticsSession()) return;
    // gtag forgets user_id on reload, unlike Rudder, so set it on every load.
    setGa4UserId(String(user.id));
    const key = 'tt_rs_identified';
    if (sessionStorage.getItem(key) === String(user.id)) return;
    identifyUser(String(user.id), {
      email: user.email,
      stripe_customer_id: user.customerId ?? undefined,
      ...getAttributionData(),
    });
    sessionStorage.setItem(key, String(user.id));
  }, [user?.id, user?.email, user?.customerId]);

  const stripeCustomerId = user?.customerId ?? null;

  const value = useObject({
    isHijacked,
    refetch,
    user,
    userLoading: loading,
    isLoggedIn,
    taxMethods,
    isTokenTaxAdmin,
    isTokenTaxDev,
    roles,
    currentUserRole,
    organizationId,
    ...batchJobsStatus,
    highestPlan,
    stripeCustomerId,
  });

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
